(() => {
  const elementName = 'hydro-oasis-shipping-estimator';
  const UNAVAILABLE_MESSAGE =
    "This option is currently unavailable, so freight can't be estimated online. Please contact us for a quote or an expected restock date.";

  if (customElements.get(elementName)) return;

  class HydroOasisShippingEstimator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.form = this.querySelector('[data-shipping-estimator-form]');
      this.postcodeInput = this.querySelector('[data-shipping-estimator-postcode]');
      this.submitButton = this.querySelector('[data-shipping-estimator-submit]');
      this.result = this.querySelector('[data-shipping-estimator-result]');

      if (!this.form || !this.postcodeInput || !this.submitButton || !this.result) return;

      this.initialized = true;
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleAccordionToggle = this.handleAccordionToggle.bind(this);
      this.handleSelectionChange = this.handleSelectionChange.bind(this);
      this.productRoot = this.closest('.main-product__blocks');
      this.form.addEventListener('submit', this.handleSubmit);
      this.productRoot?.addEventListener('click', this.handleSelectionChange);
      this.productRoot?.addEventListener('input', this.handleSelectionChange);
      this.productRoot?.addEventListener('change', this.handleSelectionChange);
      this.postcodeInput.disabled = false;
      this.submitButton.disabled = false;

      this.accordion = this.closest('details');
      if (this.accordion) {
        this.accordion.addEventListener('toggle', this.handleAccordionToggle);
        if (this.accordion.open) this.trackOpen();
      }
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.accordion?.removeEventListener('toggle', this.handleAccordionToggle);
      this.productRoot?.removeEventListener('click', this.handleSelectionChange);
      this.productRoot?.removeEventListener('input', this.handleSelectionChange);
      this.productRoot?.removeEventListener('change', this.handleSelectionChange);
      this.requestController?.abort();
      this.initialized = false;
    }

    handleAccordionToggle() {
      if (this.accordion?.open) this.trackOpen();
    }

    trackOpen() {
      if (this.openTracked) return;
      this.openTracked = true;
      this.track('freight_estimate_open');
    }

    track(eventName) {
      if (typeof window.clarity === 'function') window.clarity('event', eventName);
    }

    handleSelectionChange(event) {
      if (
        event.target === this.postcodeInput ||
        event.target?.matches(
          '.main-product__quantity input[name="quantity"], .main-product__variant-selector input, .main-product__variant-selector select'
        ) ||
        event.target?.closest(
          '.main-product__quantity button[name="plus"], .main-product__quantity button[name="minus"]'
        )
      ) {
        this.invalidateEstimate();
      }
    }

    invalidateEstimate() {
      const controller = this.requestController;
      this.requestController = null;
      controller?.abort();
      this.setLoading(false);
      this.result.replaceChildren();
      this.result.hidden = true;
    }

    getSelection() {
      const variantInput = this.productRoot?.querySelector(
        '.main-product__add-to-cart form[data-type="add-to-cart-form"] input[name="id"]'
      );
      const quantityInput = this.productRoot?.querySelector('.main-product__quantity input[name="quantity"]');
      const variantId = variantInput?.value?.trim();
      const quantity = Number.parseInt(quantityInput?.value || '1', 10);

      if (!/^\d+$/.test(variantId || '') || !Number.isInteger(quantity) || quantity < 1) {
        throw new Error('Select an available product option and quantity before estimating freight.');
      }

      return { variantId, quantity };
    }

    getSelectionKey(postcode) {
      const { variantId, quantity } = this.getSelection();
      return `${variantId}|${quantity}|${postcode}`;
    }

    async handleSubmit(event) {
      event.preventDefault();

      const postcode = this.postcodeInput.value.trim();
      this.postcodeInput.setCustomValidity(
        /^(?:02\d{2}|0[89]\d{2}|[1-9]\d{3})$/.test(postcode)
          ? ''
          : 'Enter a valid Australian postcode.'
      );

      if (!this.postcodeInput.reportValidity()) return;

      // A sold-out option has a disabled buy button; say so instead of asking Shopify for a rate.
      const buyButton = this.productRoot?.querySelector(
        '.main-product__add-to-cart form[data-type="add-to-cart-form"] button[name="add"]'
      );
      if (buyButton?.disabled) {
        this.invalidateEstimate();
        this.showError(UNAVAILABLE_MESSAGE);
        this.track('freight_estimate_unavailable');
        return;
      }

      this.requestController?.abort();
      const controller = new AbortController();
      this.requestController = controller;
      const timeoutId = window.setTimeout(() => controller.abort(), 20000);

      this.setLoading(true);
      this.showMessage('Calculating delivery options…');

      try {
        const { variantId, quantity } = this.getSelection();
        const selectionKey = `${variantId}|${quantity}|${postcode}`;
        const response = await fetch(this.dataset.endpoint || '/api/2026-07/graphql.json', {
          method: 'POST',
          credentials: 'omit',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query: `
              mutation EstimateShipping($input: CartInput!) {
                cartCreate(input: $input) {
                  cart {
                    lines(first: 10) {
                      nodes {
                        quantity
                        merchandise {
                          ... on ProductVariant {
                            id
                          }
                        }
                      }
                    }
                    deliveryGroups(first: 10) {
                      nodes {
                        deliveryOptions {
                          title
                          deliveryMethodType
                          estimatedCost {
                            amount
                            currencyCode
                          }
                        }
                      }
                    }
                  }
                  userErrors {
                    code
                    field
                    message
                  }
                  warnings {
                    code
                    message
                    target
                  }
                }
              }
            `,
            variables: {
              input: {
                lines: [
                  {
                    merchandiseId: `gid://shopify/ProductVariant/${variantId}`,
                    quantity
                  }
                ],
                buyerIdentity: {
                  countryCode: 'AU'
                },
                delivery: {
                  addresses: [
                    {
                      selected: true,
                      oneTimeUse: true,
                      address: {
                        deliveryAddress: {
                          countryCode: 'AU',
                          zip: postcode
                        }
                      }
                    }
                  ]
                }
              }
            }
          }),
          signal: controller.signal
        });

        if (!response.ok) throw new Error('Shopify could not calculate freight right now.');

        const payload = await response.json();
        const userErrors = payload?.data?.cartCreate?.userErrors || [];
        const warnings = payload?.data?.cartCreate?.warnings || [];
        const graphErrors = payload?.errors || [];

        const stockCodes = /^(MERCHANDISE_OUT_OF_STOCK|MERCHANDISE_NOT_ENOUGH_STOCK|PRODUCT_UNAVAILABLE_IN_BUYER_LOCATION|INVALID_MERCHANDISE_LINE)$/;
        const stockText = /sold out|out of stock|not enough|unavailable|not available/i;
        const stockProblem = [...warnings, ...userErrors].some(
          (issue) => stockCodes.test(issue?.code || '') || stockText.test(issue?.message || '')
        );
        if (stockProblem) {
          throw new Error(
            quantity > 1
              ? "This quantity isn't available, so freight can't be estimated online. Try a smaller quantity or contact us for a quote."
              : UNAVAILABLE_MESSAGE
          );
        }

        if (userErrors.length || warnings.length || graphErrors.length) {
          throw new Error('Shopify could not calculate freight for that postcode.');
        }

        const returnedLines = payload?.data?.cartCreate?.cart?.lines?.nodes || [];
        const expectedVariantId = `gid://shopify/ProductVariant/${variantId}`;
        const exactLineMatch =
          returnedLines.length === 1 &&
          returnedLines[0]?.merchandise?.id === expectedVariantId &&
          returnedLines[0]?.quantity === quantity;

        if (!exactLineMatch) {
          throw new Error('Shopify could not estimate the selected quantity. Please verify availability at checkout.');
        }

        if (controller !== this.requestController) return;

        let currentSelectionKey;
        try {
          currentSelectionKey = this.getSelectionKey(this.postcodeInput.value.trim());
        } catch {
          return;
        }

        if (selectionKey !== currentSelectionKey) return;

        const groups = payload?.data?.cartCreate?.cart?.deliveryGroups?.nodes || [];
        const shippingGroups = groups
          .map((group) => (group.deliveryOptions || []).filter((option) => option.deliveryMethodType === 'SHIPPING'))
          .filter((options) => options.length);

        if (shippingGroups.length > 1) {
          this.showError('This item requires more than one shipment. Please use checkout for the final freight total.');
          this.track('freight_estimate_no_rate');
          return;
        }

        const options = shippingGroups[0] || [];
        const uniqueOptions = Array.from(
          new Map(
            options.map((option) => [
              `${option.title}|${option.estimatedCost?.amount}|${option.estimatedCost?.currencyCode}`,
              option
            ])
          ).values()
        ).sort(
          (left, right) =>
            Number(left.estimatedCost?.amount || 0) - Number(right.estimatedCost?.amount || 0)
        );

        if (!uniqueOptions.length) {
          this.showError('No online freight rate is available for this item and postcode. Please contact us for a quote.');
          this.track('freight_estimate_no_rate');
          return;
        }

        this.showRates(uniqueOptions);
        this.track('freight_estimate_success');
      } catch (error) {
        if (controller !== this.requestController) return;

        const message =
          error?.name === 'AbortError'
            ? 'The freight estimate took too long. Please try again.'
            : error?.message || 'Freight could not be estimated right now. Please try again.';
        this.showError(message);
        this.track('freight_estimate_error');
      } finally {
        window.clearTimeout(timeoutId);
        if (controller === this.requestController) {
          this.requestController = null;
          this.setLoading(false);
        }
      }
    }

    setLoading(isLoading) {
      this.result.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      this.submitButton.disabled = isLoading;
      this.submitButton.textContent = isLoading ? 'Estimating…' : 'Estimate freight';
    }

    showMessage(message) {
      this.result.classList.remove('shipping-estimator__result--error');
      this.result.replaceChildren(document.createTextNode(message));
      this.result.hidden = false;
    }

    showError(message) {
      this.result.classList.add('shipping-estimator__result--error');
      this.result.replaceChildren(document.createTextNode(message));
      this.result.hidden = false;
    }

    showRates(options) {
      const list = document.createElement('ul');
      list.className = 'shipping-estimator__rates';

      options.forEach((option) => {
        const item = document.createElement('li');
        const title = document.createElement('span');
        const price = document.createElement('span');
        const quoteRequired = /freight quote required/i.test(option.title || '');
        const amount = Number(option.estimatedCost?.amount);
        const currency = option.estimatedCost?.currencyCode || 'AUD';

        item.className = 'shipping-estimator__rate';
        title.textContent = option.title || 'Delivery';
        price.className = 'shipping-estimator__rate-price';
        price.textContent = quoteRequired
          ? 'Quote required'
          : Number.isFinite(amount)
            ? new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(amount)
            : 'See checkout';

        item.append(title, price);
        list.append(item);
      });

      this.result.classList.remove('shipping-estimator__result--error');
      this.result.replaceChildren(list);
      this.result.hidden = false;
    }
  }

  customElements.define(elementName, HydroOasisShippingEstimator);
})();
